import { useId, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "./cx";
import { progressPercent } from "./format";
import "./ProgressBar.css";

export interface ProgressBarProps extends HTMLAttributes<HTMLDivElement> {
  /** Omit (or pass null) for the indeterminate state. */
  value?: number | null;
  max?: number;
  /** Visible label, e.g. the job name. Required for an accessible name. */
  label: ReactNode;
  /** Text read by AT and shown on the right, e.g. "40 / 312 channels". */
  valueText?: string;
  /** Show the value (valueText or percentage) next to the label. Default true. */
  showValue?: boolean;
  tone?: "accent" | "success" | "warning" | "danger";
  size?: "sm" | "md";
}

/** Job / bulk-operation progress. role="progressbar" with value text. */
export function ProgressBar({
  value,
  max = 100,
  label,
  valueText,
  showValue = true,
  tone = "accent",
  size = "md",
  className,
  ...rest
}: ProgressBarProps) {
  const auto = useId();
  const labelId = `pb${auto}`;
  const indeterminate = value === undefined || value === null;
  const pct = indeterminate ? 0 : progressPercent(value, max);
  const text = indeterminate ? undefined : (valueText ?? `${Math.round(pct)}%`);

  return (
    <div
      className={cx(
        "ui-progress",
        `ui-progress--${tone}`,
        `ui-progress--${size}`,
        indeterminate && "ui-progress--indeterminate",
        className,
      )}
      {...rest}
    >
      <div className="ui-progress__head">
        <span className="ui-progress__label" id={labelId}>
          {label}
        </span>
        {showValue && text ? <span className="ui-progress__value">{text}</span> : null}
      </div>
      <div
        className="ui-progress__track"
        role="progressbar"
        aria-labelledby={labelId}
        aria-valuemin={indeterminate ? undefined : 0}
        aria-valuemax={indeterminate ? undefined : max}
        aria-valuenow={indeterminate ? undefined : value}
        aria-valuetext={text}
        aria-busy={indeterminate || undefined}
      >
        <div
          className="ui-progress__fill"
          style={indeterminate ? undefined : { width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
